import React, { useState } from 'react';
import { Dialog, DialogContent, Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OrganizationUpgradeModal from './OrganizationUpgradeModal';

// Organization icon SVG
const OrgIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="9" cy="7" r="4" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Lock icon SVG
const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="11" width="18" height="11" rx="2" stroke="#617BFF" strokeWidth="2"/>
    <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="#617BFF" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="12" cy="16" r="1.5" fill="#617BFF"/>
  </svg>
);

interface FeatureItem {
  title: string;
  description: string;
}

interface UpgradePromptModalProps {
  open: boolean;
  onClose: () => void;
  featureName: string;
  title?: string;
  description?: string;
  features?: FeatureItem[];
}

const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
  open,
  onClose,
  featureName,
  title = 'Organization Account Required',
  description = 'This feature is only available for organization accounts. Upgrade to unlock team collaboration, advanced security features, and more.',
  features,
}) => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleUpgrade = () => {
    setShowUpgradeModal(true);
  };

  const handleLearnMore = () => {
    window.open('https://docs.flashback.tech/organization', '_blank', 'noopener,noreferrer');
  };

  const handleUpgradeSuccess = () => {
    setShowUpgradeModal(false);
    onClose();
  };

  // Get features based on featureName
  const getFeatures = (): FeatureItem[] => {
    if (features) return features;
    
    if (featureName === 'Private Chat') {
      return [
        { title: 'Secure AI Conversations', description: 'End-to-end encrypted chat with enterprise-grade AI models' },
        { title: 'Organization-Scoped Environment', description: 'Your data stays private and is never used for model training' },
        { title: 'Multi-Model Support', description: 'Access to OpenAI, Anthropic, and other leading AI providers' },
      ];
    }
    
    if (featureName === 'Organization') {
      return [
        { title: 'Team Management', description: 'Invite team members and manage roles & permissions' },
        { title: 'Centralized Billing', description: 'Single invoice for all team usage and subscriptions' },
        { title: 'Advanced Security', description: 'SSO, audit logs, and compliance controls' },
      ];
    }
    
    return [
      { title: 'Team Collaboration', description: 'Work together with your team on shared resources' },
      { title: 'Advanced Security', description: 'Enterprise-grade security and access controls' },
      { title: 'Centralized Management', description: 'Manage users, billing, and settings from one place' },
    ];
  };

  const displayFeatures = getFeatures();

  return (
    <>
      <Dialog
        open={open && !showUpgradeModal}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxWidth: '480px',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Close button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: '#9CA3AF',
              '&:hover': { bgcolor: '#F3F4F6' },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ p: 4, textAlign: 'center' }}>
            {/* Icon container */}
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(97, 123, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1,
                position: 'relative',
              }}
            >
              <OrgIcon />
              {/* Lock badge */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: 'white',
                  border: '2px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LockIcon />
              </Box>
            </Box>

            {/* Title */}
            <Typography
              sx={{
                fontSize: '22px',
                fontWeight: 700,
                color: '#181D27',
                mt: 3,
                mb: 1.5,
              }}
            >
              {title}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#535862',
                lineHeight: 1.6,
                mb: 3,
              }}
            >
              {description}
            </Typography>

            {/* Features list */}
            <Box sx={{ textAlign: 'left', mb: 3 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#181D27',
                  mb: 2,
                }}
              >
                With {featureName}, you will be able to:
              </Typography>
              {displayFeatures.map((feature, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: 'rgba(97, 123, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mb: 0.25 }}>
                      {feature.title}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#535862', lineHeight: 1.4 }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* CTA Button */}
            <Box sx={{ width: '100%' }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleUpgrade}
                sx={{
                  textTransform: 'none',
                  borderRadius: '10px',
                  bgcolor: '#617BFF',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '14px',
                  py: 1.25,
                  boxShadow: '0 4px 12px rgba(97, 123, 255, 0.3)',
                  '&:hover': {
                    bgcolor: '#5A6FE8',
                    boxShadow: '0 6px 16px rgba(97, 123, 255, 0.4)',
                  },
                }}
              >
                Switch to Organization
              </Button>
            </Box>

            {/* Contact sales link */}
            <Typography
              sx={{
                fontSize: '12px',
                color: '#717680',
                mt: 2,
              }}
            >
              Need help?{' '}
              <Box
                component="a"
                href="mailto:support@flashback.tech"
                sx={{
                  color: '#617BFF',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Contact our team
              </Box>
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Upgrade Modal (step 2) */}
      <OrganizationUpgradeModal
        open={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          onClose();
        }}
        featureName={featureName}
        onSuccess={handleUpgradeSuccess}
      />
    </>
  );
};

export default UpgradePromptModal;
